import React from 'react';

function renderInline(text, keyPrefix) {
  const parts = text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)\s]+\))/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={`${keyPrefix}-${i}`} className="text-white font-semibold">
          {part.slice(2, -2)}
        </strong>
      );
    }
    const link = part.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/);
    if (link) {
      const href = link[2];
      const external = href.startsWith('http');
      return (
        <a
          key={`${keyPrefix}-${i}`}
          href={href}
          {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
          className="text-[#e8173a] underline underline-offset-4 decoration-[#e8173a]/40 hover:decoration-[#e8173a] transition-colors"
        >
          {link[1]}
        </a>
      );
    }
    return <React.Fragment key={`${keyPrefix}-${i}`}>{part}</React.Fragment>;
  });
}

export default function Markdown({ content }) {
  if (!content) return null;
  const lines = content.split('\n');
  const blocks = [];
  let list = [];
  let listKey = 0;

  const flushList = () => {
    if (list.length) {
      blocks.push(
        <ul key={`ul-${listKey++}`} className="list-disc pl-5 space-y-2 text-[#c9c9c9] marker:text-[#e8173a]">
          {list.map((item, i) => (
            <li key={i}>{renderInline(item, `li-${listKey}-${i}`)}</li>
          ))}
        </ul>
      );
      list = [];
    }
  };

  lines.forEach((raw, idx) => {
    const line = raw.trim();
    if (!line) {
      flushList();
      return;
    }
    if (line.startsWith('### ')) {
      flushList();
      blocks.push(
        <h3 key={idx} className="text-base md:text-lg font-bold text-white mt-8 mb-3">
          {renderInline(line.slice(4), `h3-${idx}`)}
        </h3>
      );
    } else if (line.startsWith('## ')) {
      flushList();
      blocks.push(
        <h2 key={idx} className="text-lg md:text-xl font-extrabold italic text-[#e8173a] mt-10 mb-4">
          {renderInline(line.slice(3), `h2-${idx}`)}
        </h2>
      );
    } else if (line.startsWith('# ')) {
      flushList();
      blocks.push(
        <h1 key={idx} className="text-2xl md:text-3xl font-black italic text-white mt-2 mb-6 tracking-tight">
          {renderInline(line.slice(2), `h1-${idx}`)}
        </h1>
      );
    } else if (/^[-*]\s+/.test(line)) {
      list.push(line.replace(/^[-*]\s+/, ''));
    } else if (/^\d+\.\s+/.test(line)) {
      flushList();
      blocks.push(
        <p key={idx} className="text-[#c9c9c9] leading-relaxed mb-3 pl-4 border-l-2 border-[#e8173a]/40">
          {renderInline(line, `ol-${idx}`)}
        </p>
      );
    } else {
      flushList();
      blocks.push(
        <p key={idx} className="text-[#c9c9c9] leading-relaxed mb-3">
          {renderInline(line, `p-${idx}`)}
        </p>
      );
    }
  });
  flushList();

  return <div data-testid="markdown-content">{blocks}</div>;
}
